import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Governance Readout Center (PRD GV-001…GV-054).
 * Mirrors the as-built Diagnosis/Metrics view-switcher: chips set an active view; inactive views
 * stay mounted via the `hidden` attribute; an Expand all / Collapse toggle (sibling OUTSIDE the
 * tablist, `aria-pressed`) reveals every view. All copy is seeded/derived; pure render functions.
 * The owning GovernanceTab does the data derivation (barriers, decisions, closeout gates) and
 * passes arrays in, so this module stays presentation-only.
 */

export const GOVERNANCE_SECTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'decisions', label: 'Decisions' },
  { key: 'board-packet', label: 'Board Packet' },
  { key: 'evidence-lineage', label: 'Evidence & Lineage' },
];

// ---------------------------------------------------------------------------
// Derivations (seed-bound; no hardcoded counts)
// ---------------------------------------------------------------------------

const NEEDS_DECISION_STATUSES = ['Open', 'Watch'];

// Sponsor decision questions = open/watch barriers carrying a decisionNeeded. This is the ONLY
// source of the "Decisions needed" headline count (seeded baseline = 4: B-26-001…004).
export function deriveDecisionsNeeded(barriers = []) {
  return barriers.filter(
    (barrier) => NEEDS_DECISION_STATUSES.includes(barrier.status) && barrier.decisionNeeded,
  );
}

function slug(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Closeout gates to clear = required, not-done checklist items. Separate group; NOT in the count.
export function deriveCloseoutGates(closeout, validationStatus) {
  const checklist = closeout?.closeoutReadiness?.checklist || [];
  return checklist
    .filter((item) => item.requiredForUnblock && !item.done)
    .map((item) => {
      const isSponsorValidation = /sponsor validation/i.test(item.item);
      return {
        id: isSponsorValidation ? 'closeout-sponsor-validation' : slug(item.item),
        label: item.item,
        isSponsorValidation,
        statusLabel: isSponsorValidation
          ? `Sponsor validation: ${validationStatus || 'Pending'}`
          : 'Required to unblock closeout',
      };
    });
}

// "Draft ready" from role-lens brief-readiness, else seeded briefing status, else not-generated.
export function deriveBriefStatus(roleViews, briefings = [], generatedBrief) {
  if (generatedBrief) return 'Preview generated';
  const leadCards = roleViews?.roles?.['RAPID Lead']?.topCards || [];
  const readinessCard = leadCards.find((card) => /brief readiness/i.test(card));
  if (readinessCard) return readinessCard.replace(/^.*brief readiness:\s*/i, '').trim();
  if (briefings.length) return briefings[0].status || 'Preview';
  return 'Preview not generated';
}

// Deterministic readout-status label (no free-floating sentence).
export function deriveReadoutStatus({
  validationPending,
  openDecisions,
  briefGenerated,
  gatesRemaining,
}) {
  if (validationPending && openDecisions > 0) return 'Draft-ready, sponsor decisions pending';
  if (briefGenerated && gatesRemaining > 0)
    return 'Brief preview generated; closeout still blocked';
  if (!validationPending && gatesRemaining === 0) return 'Sponsor-ready';
  return 'Draft-ready, sponsor decisions pending';
}

// ---------------------------------------------------------------------------
// View-switcher nav (mirrors MetricsSectionNav; Expand all OUTSIDE the tablist)
// ---------------------------------------------------------------------------

export function GovernanceSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="governance-section-nav" data-testid="governance-section-nav">
      <div className="governance-nav-tabs" role="tablist" aria-label="Governance sections">
        {GOVERNANCE_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`governance-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`governance-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`governance-nav-chip governance-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="governance-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// 7.1 Always-on Governance Summary
// ---------------------------------------------------------------------------

export function GovernanceSummaryPanel({ stats, readoutStatus, sayCaveatAsk }) {
  return (
    <section
      className="rapid-panel governance-summary-panel"
      data-testid="governance-summary-panel"
    >
      <div className="governance-summary-head">
        <div className="eyebrow">Governance readout</div>
        <h3 className="governance-summary-headline" data-testid="governance-summary-readout-status">
          {readoutStatus}
        </h3>
      </div>

      <div className="governance-summary-stats">
        <div className="governance-summary-stat is-primary">
          <span className="k">Decisions needed</span>
          <span className="v" data-testid="governance-summary-decisions-needed">
            {stats.decisionsNeeded}
          </span>
          <span className="sub">sponsor decision questions</span>
        </div>
        <div className="governance-summary-stat">
          <span className="k">Open blockers</span>
          <span className="v" data-testid="governance-summary-open-blockers">
            {stats.openBlockers}
          </span>
          <span className="sub">open / watch barriers</span>
        </div>
        <div className="governance-summary-stat">
          <span className="k">Brief status</span>
          <span className="v is-text" data-testid="governance-summary-brief-status">
            {stats.briefStatus}
          </span>
          <span className="sub">governance preview</span>
        </div>
        <div className="governance-summary-stat">
          <span className="k">Closeout readiness</span>
          <span className="v" data-testid="governance-summary-closeout">
            {stats.closeoutPercent}%
          </span>
          <span className="sub">
            {stats.closeoutComplete} / {stats.closeoutTotal} complete
          </span>
        </div>
      </div>

      <div className="governance-summary-notes">
        <div
          className="governance-summary-note is-say"
          data-testid="governance-summary-what-to-say"
        >
          <h4>What to say</h4>
          <p>{sayCaveatAsk.say}</p>
        </div>
        <div
          className="governance-summary-note is-caveat"
          data-testid="governance-summary-what-to-caveat"
        >
          <h4>What to caveat</h4>
          <p>{sayCaveatAsk.caveat}</p>
        </div>
        <div
          className="governance-summary-note is-ask"
          data-testid="governance-summary-executive-ask"
        >
          <h4>Executive ask</h4>
          <p>{sayCaveatAsk.ask}</p>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.3 Summary view
// ---------------------------------------------------------------------------

export function GovernanceSummaryView({ sayCaveatAsk, nextCheckpoint, onOpenCloseout }) {
  return (
    <section className="rapid-panel governance-summary-view" data-testid="governance-summary-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Sponsor readout</div>
          <h3>What to say, caveat, and ask</h3>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          data-testid="governance-open-closeout"
          onClick={onOpenCloseout}
        >
          Open Closeout / Handoff
        </button>
      </div>
      <div className="governance-readout-grid">
        <div className="governance-readout-block is-say" data-testid="governance-summary-view-say">
          <h4>What to say</h4>
          <p>{sayCaveatAsk.say}</p>
        </div>
        <div
          className="governance-readout-block is-caveat"
          data-testid="governance-summary-view-caveat"
        >
          <h4>What to caveat</h4>
          <p>{sayCaveatAsk.caveat}</p>
        </div>
        <div className="governance-readout-block is-ask" data-testid="governance-summary-view-ask">
          <h4>Executive ask</h4>
          <p>{sayCaveatAsk.ask}</p>
        </div>
      </div>
      <div className="governance-next-checkpoint">
        <span className="chip outline sm">Next governance checkpoint</span>
        <p>{nextCheckpoint}</p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.4 Decisions view — three groups
// ---------------------------------------------------------------------------

function DecisionNeededCard({ barrier }) {
  return (
    <article
      className="governance-decision-card is-needed"
      data-testid={`governance-needed-${barrier.id}`}
    >
      <div className="row spread">
        <strong>{barrier.barrier}</strong>
        <StatusChip status={barrier.status}>{barrier.status}</StatusChip>
      </div>
      <p className="governance-decision-question">
        <span className="k">Decision needed:</span> {barrier.decisionNeeded}
      </p>
      <div className="governance-decision-meta muted small">
        <span>{barrier.id}</span>
        {barrier.owner ? <span>· Owner: {barrier.owner}</span> : null}
        {barrier.severity ? <span>· {barrier.severity}</span> : null}
      </div>
      <p className="governance-decision-move">
        <span className="k">Recommended move:</span> Route to {barrier.owner || 'the named owner'}{' '}
        for a sponsor decision.
      </p>
    </article>
  );
}

function DecisionMadeCard({ decision }) {
  const label = decision.decisionType || decision.status;
  return (
    <article
      className="governance-decision-card is-made"
      data-testid={`governance-decision-${decision.id}`}
    >
      <div className="row spread">
        <strong>
          {decision.id} · {decision.title || decision.decision}
        </strong>
        <StatusChip status={label}>{label}</StatusChip>
      </div>
      {decision.sourceFindingId || decision.source ? (
        <div className="muted small">Source: {decision.sourceFindingId || decision.source}</div>
      ) : null}
      {decision.rationale ? <p>{decision.rationale}</p> : null}
    </article>
  );
}

export function GovernanceDecisionsView({
  decisionsNeeded,
  closeoutGates,
  decisionsMade,
  onOpenCloseout,
}) {
  return (
    <section
      className="rapid-panel governance-decisions-view"
      data-testid="governance-decisions-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Decisions</div>
          <h3>What needs sponsor judgment vs what is already decided</h3>
        </div>
      </div>

      <div className="governance-decisions-grid">
        <div className="governance-decisions-col">
          <div className="governance-group-head">
            <h4>Sponsor decision questions</h4>
            <span className="chip solid sm">{decisionsNeeded.length} needed</span>
          </div>
          <div className="governance-decision-list" data-testid="governance-decisions-needed">
            {decisionsNeeded.map((barrier) => (
              <DecisionNeededCard key={barrier.id} barrier={barrier} />
            ))}
          </div>

          <div className="governance-group-head">
            <h4>Closeout gates to clear</h4>
            <span className="chip outline sm">{closeoutGates.length} gates</span>
          </div>
          <ul className="governance-gate-list" data-testid="governance-closeout-gates">
            {closeoutGates.map((gate) => (
              <li
                className="governance-gate"
                data-testid={`governance-gate-${gate.id}`}
                key={gate.id}
              >
                <div className="governance-gate-body">
                  <strong>{gate.label}</strong>
                  <span className="muted small">{gate.statusLabel}</span>
                </div>
                {gate.isSponsorValidation ? (
                  <button
                    type="button"
                    className="btn ghost xs"
                    data-testid="governance-gate-open-closeout"
                    onClick={onOpenCloseout}
                  >
                    Open Closeout
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="muted small governance-gate-note">
            Closeout gates are operational unblock items — they are not counted in the{' '}
            {decisionsNeeded.length} sponsor decisions above.
          </p>
        </div>

        <div className="governance-decisions-col">
          <div className="governance-group-head">
            <h4>Decisions made</h4>
            <span className="chip outline sm">{decisionsMade.length} logged</span>
          </div>
          <div className="governance-decision-list" data-testid="governance-decisions-made">
            {decisionsMade.map((decision) => (
              <DecisionMadeCard key={decision.id} decision={decision} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.5 Board Packet view — owns the Generate CTA, preserves the brief contract
// ---------------------------------------------------------------------------

function PacketRow({ testId, label, children }) {
  return (
    <div className="governance-packet-row" data-testid={testId}>
      <div className="governance-packet-label">{label}</div>
      <div className="governance-packet-value">{children}</div>
    </div>
  );
}

export function BoardPacketView({
  detail,
  barriers,
  decisionsMade,
  onGenerateBrief,
  generatingBrief,
  generatedBrief,
}) {
  const rapid = detail.rapid;
  const closeout = rapid.closeout?.closeoutReadiness;
  return (
    <section
      className="rapid-panel governance-board-packet-view"
      data-testid="governance-board-packet-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Board packet</div>
          <h3>Sponsor-ready governance brief</h3>
        </div>
        <div className="governance-packet-actions">
          <button
            className="btn primary sm"
            type="button"
            data-testid="governance-generate-brief"
            onClick={onGenerateBrief}
            disabled={generatingBrief}
          >
            {generatingBrief ? 'Generating…' : 'Generate Governance Brief Preview'}
          </button>
        </div>
      </div>

      {generatedBrief ? (
        <div className="governance-board-packet" data-testid="governance-board-packet">
          <header className="governance-packet-header">
            <div>
              <div className="eyebrow">{rapid.id}</div>
              <h4>{rapid.title}</h4>
            </div>
            <div className="rapid-chip-row">
              <StatusChip status={rapid.healthOverall}>{rapid.healthOverall}</StatusChip>
              <span className="chip outline sm">{rapid.currentPhaseLabel || rapid.phase}</span>
              <span className="chip outline sm">Closeout {closeout?.percent}%</span>
            </div>
          </header>

          <PacketRow testId="governance-packet-bluf" label="BLUF">
            {rapid.summary}
          </PacketRow>
          <PacketRow testId="governance-packet-phase" label="Phase">
            {rapid.currentPhaseLabel || rapid.phase}
          </PacketRow>
          <PacketRow testId="governance-packet-pod-status" label="Pod status">
            {(detail.pods || []).map((pod) => `${pod.id}: ${pod.healthOverall}`).join(' · ')}
          </PacketRow>
          <PacketRow testId="governance-packet-blockers" label="Blockers">
            {barriers.map((barrier) => barrier.id).join(', ')}
          </PacketRow>
          <PacketRow testId="governance-packet-decisions" label="Decisions">
            {decisionsMade.map((decision) => decision.id).join(', ')}
          </PacketRow>
          <PacketRow testId="governance-packet-metrics" label="Metrics">
            {(detail.metrics || [])
              .slice(0, 3)
              .map((metric) => metric.id)
              .join(', ')}
          </PacketRow>
          <PacketRow testId="governance-packet-way-ahead" label="Way ahead">
            {rapid.bowlerChart?.wayAhead}
          </PacketRow>

          <div className="governance-packet-footer">
            <span className="chip outline sm" data-testid="governance-export-packet">
              Export / HTML briefing — Preview
            </span>
            <span className="muted small">
              Preview packet. Full export is a Preview capability.
            </span>
          </div>
        </div>
      ) : (
        <div className="rapid-empty-inline" data-testid="governance-board-packet-empty">
          Generate the preview to assemble the board packet (BLUF, status, decisions, metrics, way
          ahead).
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.6 Evidence & Lineage view
// ---------------------------------------------------------------------------

export function EvidenceLineageView({ briefings = [], evidenceLinks = [], children }) {
  return (
    <section
      className="rapid-panel governance-evidence-lineage-view"
      data-testid="governance-evidence-lineage-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Evidence &amp; lineage</div>
          <h3>Proof supporting the governance readout</h3>
        </div>
      </div>

      <div className="governance-evidence-grid">
        <div className="governance-evidence-col">
          <h4>Artifact outputs</h4>
          {briefings.length ? (
            <ul className="rapid-record-list" data-testid="governance-briefing-artifacts">
              {briefings.map((briefing) => (
                <li key={briefing.id}>
                  <strong>{briefing.title}</strong>
                  <div className="muted small">
                    {briefing.id} · {briefing.format} · {briefing.status}
                  </div>
                  {briefing.summary ? <p>{briefing.summary}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">No briefing artifacts generated yet (Preview).</p>
          )}
        </div>
        <div className="governance-evidence-col">
          <h4>Source support</h4>
          {evidenceLinks.length ? (
            <ul className="rapid-record-list" data-testid="governance-evidence-links">
              {evidenceLinks.map((link) => (
                <li key={`${link.claim}-${link.source}`}>
                  <strong>{link.claim}</strong>
                  <div className="muted small">
                    {link.type} · {link.source}
                  </div>
                  {link.note ? <p>{link.note}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">No evidence links for this section.</p>
          )}
        </div>
      </div>

      <div className="governance-lineage-wrap" data-testid="governance-lineage-preview">
        {children}
      </div>
    </section>
  );
}
